import { Field } from '../../../../../../../../settings/types/field';
import { Attributes } from '../../../../../../../types';
import { adjustImageSizes } from './adjust-image-sizes';
import { getMergeTagsMappingWithValues } from './get-merge-tag-mapping-with-values';
import { getVariableTagsMappingWithValues } from './get-variable-tag-mapping-with-values';
import {
  convertHtmlToText,
  parseContentForTextOnlySequence,
} from './get-parsed-content-for-text-only-email';
import { replaceAttributesWithValues } from './replace-attributes-with-value';
import { getParsedSpinText } from './get-parsed-spin-text';
import { Schedule } from '../../../../../../../../settings/types/schedule';
import { TextOnlyEmailSettingValue } from '../../../../../../../enums';

type GenerateEmailPreviewProps = {
  content: string;
  attributes: Attributes[];
  mergeTags: Field[];
  unsubscribeLink: string;
  unsubscribeText: string;
  textOnlyEmail: string;
  doNotAddSignatureAndUnsubscribeLink?: boolean;
  doNotAdjustImages?: boolean;
  senderDetails: {
    firstName: string;
    lastName: string;
    email: string;
    signature: string;
  };
  sequenceSchedule: Schedule;
  disabledVariableTags: string[];
  textOnlyEmailEnabledForFirstStep?: boolean;
};

export const addTargetBlankToAnchorTag = (content) =>
  content?.replace(/<a\s+([^>]*)>/gi, '<a target="_blank" $1>');

const isLinkForMailOrTel = (link) =>
  link.startsWith('mailto:') || link.startsWith('tel:');

const isLinkValid = (link) =>
  link.startsWith('http://') || link.startsWith('https://');

const extractBodyContent = (html: string): string => {
  if (!html) {
    return html;
  }
  const hasDocumentWrapper = /<\/?(html|body)[^>]*>/i.test(html);
  if (!hasDocumentWrapper) {
    return html;
  }

  if (typeof DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const parsed = parser.parseFromString(html, 'text/html');
      const bodyHtml = parsed?.body?.innerHTML?.trim();
      if (bodyHtml) {
        return bodyHtml;
      }
    } catch (error) {
      // no-op: fall through to regex-based cleanup
    }
  }

  return html
    ?.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    ?.replace(/<\/?html[^>]*>/gi, '')
    ?.replace(/<\/?body[^>]*>/gi, '');
};

const promoteTitleTags = (html: string): string =>
  html?.replace(/<title[^>]*>([\s\S]*?)<\/title>/gi, (_, title = '') => {
    const cleanedTitle = title.trim();
    if (!cleanedTitle) {
      return '';
    }

    return `<div >${cleanedTitle}</div>`;
  });

export const addHttpInHrefOfAnchorTag = (content = '') =>
  content?.replace(
    /<a\s+([^>]*)href="((?!http[s]?:\/\/)[^"]*)"/gi,
    (match, attributes, href) => {
      const modifiedHref =
        isLinkValid(href) || isLinkForMailOrTel(href) ? href : `http://${href}`;
      return `<a ${attributes} href="${modifiedHref}"`;
    },
  );

const cleanAnchorTagHrefValue = (tag: string): string =>
  tag.replace(
    /<a\s+(?:[^>]*?\s+)?href\s*=\s*(['"])(.*?)\1/gi,
    '<a class="non-clickable-link"',
  );

export const findAndCleanAnchorTag = (content: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  // Remove all <base> tags
  const baseTags = tempDiv.querySelectorAll('base');
  baseTags?.forEach((baseTag) => baseTag.remove());

  // Remove all style tags
  const styleTags = tempDiv.querySelectorAll('style');
  styleTags?.forEach((styleTag) => styleTag.remove());

  // Find all anchor tags in the temporary div
  const anchorTags = tempDiv.getElementsByTagName('a');

  if (anchorTags?.length > 0) {
    // Get the last anchor tag
    const lastAnchorTag = anchorTags[anchorTags.length - 1];

    const modifiedAnchorTag = cleanAnchorTagHrefValue(lastAnchorTag.outerHTML);

    lastAnchorTag.outerHTML = modifiedAnchorTag;

    // Update the htmlString with the modified HTML content
    const modifiedHtmlString = tempDiv.innerHTML;

    return modifiedHtmlString;
  }

  return tempDiv.innerHTML;
};

export const generateEmailPreview = ({
  content,
  mergeTags,
  attributes,
  unsubscribeLink,
  unsubscribeText,
  textOnlyEmail,
  doNotAddSignatureAndUnsubscribeLink = false,
  doNotAdjustImages = false,
  senderDetails,
  sequenceSchedule,
  disabledVariableTags = [],
  textOnlyEmailEnabledForFirstStep = false,
}: GenerateEmailPreviewProps): string => {
  let parsedContent = extractBodyContent(content);

  const variableTagsMappingWithValues = getVariableTagsMappingWithValues(
    parsedContent,
    senderDetails,
    sequenceSchedule?.timezone ?? '',
  );

  let attributesMappingForRegex = {};
  let attributesMappingWithValues = {};

  if (attributes && attributes?.length > 0) {
    const {
      mergeTagsMappingForRegex,
      mergeTagsMappingWithValues,
    } = getMergeTagsMappingWithValues(mergeTags, attributes);

    attributesMappingForRegex = {
      ...variableTagsMappingWithValues,
      ...mergeTagsMappingForRegex,
    };

    attributesMappingWithValues = {
      ...variableTagsMappingWithValues,
      ...mergeTagsMappingWithValues,
    };

    Object.keys(attributesMappingWithValues).forEach((key) => {
      if (attributesMappingWithValues[key] === '') {
        attributesMappingWithValues[key] = variableTagsMappingWithValues[key];
      }
    });
  } else {
    attributesMappingForRegex = {
      ...variableTagsMappingWithValues,
    };

    attributesMappingWithValues = {
      ...variableTagsMappingWithValues,
    };
  }

  // Parse spin tax BEFORE replacing attributes to protect merge tags with pipes
  const contentMergeTagKeys = Object.keys(
    attributesMappingForRegex,
  )?.map((key) => key.toLowerCase());
  parsedContent = getParsedSpinText(
    parsedContent,
    contentMergeTagKeys,
    mergeTags,
  );

  parsedContent = replaceAttributesWithValues(
    parsedContent,
    attributesMappingForRegex,
    attributesMappingWithValues,
    'body',
  );

  parsedContent = addTargetBlankToAnchorTag(
    addHttpInHrefOfAnchorTag(parsedContent),
  );

  parsedContent = promoteTitleTags(parsedContent);

  if (!doNotAdjustImages) {
    parsedContent = adjustImageSizes(parsedContent);
  }

  let contentToRender = `${parsedContent} ${
    !doNotAddSignatureAndUnsubscribeLink &&
    senderDetails.signature &&
    !disabledVariableTags.includes('{{SenderSignature}}')
      ? `<br> <div dir="ltr">${senderDetails.signature}</div>`
      : ''
  }`;

  if (!doNotAddSignatureAndUnsubscribeLink) {
    if (unsubscribeLink) {
      contentToRender = `${contentToRender} 
        <br /> <span style="font-size:12px">${cleanAnchorTagHrefValue(
          unsubscribeLink,
        )}</span>
      `;
    } else if (unsubscribeText) {
      contentToRender = `${contentToRender} 
      <br /> <span style="font-size:12px">${unsubscribeText}</span>
    `;
    }
  }

  if (
    textOnlyEmail === TextOnlyEmailSettingValue.TEXT_ONLY_EMAIL ||
    textOnlyEmailEnabledForFirstStep
  ) {
    return convertHtmlToText(contentToRender);
  }

  if (
    textOnlyEmail === TextOnlyEmailSettingValue.REMOVE_STYLING_AUTOMATICALLY
  ) {
    return parseContentForTextOnlySequence(contentToRender);
  }

  return contentToRender;
};

export const getParsedEmailSubject = ({
  subject,
  prevStepSubject,
  mergeTags,
  attributes,
  senderDetails,
  sequenceSchedule,
}): string => {
  const getParsedSubject = (value) => {
    let parsedSubject = value;

    const variableTagsMappingWithValues = getVariableTagsMappingWithValues(
      parsedSubject,
      senderDetails,
      sequenceSchedule?.timezone ?? '',
    );

    let attributesMappingForRegex = {};
    let attributesMappingWithValues = {};

    if (attributes && attributes?.length > 0) {
      const {
        mergeTagsMappingForRegex,
        mergeTagsMappingWithValues,
      } = getMergeTagsMappingWithValues(mergeTags, attributes);

      attributesMappingForRegex = {
        ...variableTagsMappingWithValues,
        ...mergeTagsMappingForRegex,
      };

      attributesMappingWithValues = {
        ...variableTagsMappingWithValues,
        ...mergeTagsMappingWithValues,
      };

      Object.keys(attributesMappingWithValues).forEach((key) => {
        if (attributesMappingWithValues[key] === '') {
          attributesMappingWithValues[key] = variableTagsMappingWithValues[key];
        }
      });
    } else {
      attributesMappingForRegex = {
        ...variableTagsMappingWithValues,
      };

      attributesMappingWithValues = {
        ...variableTagsMappingWithValues,
      };
    }

    // Create subject attribute map excluding SenderSignature (removing signature tag for subject)
    const subjectAttrMap = {
      ...attributesMappingForRegex,
    };
    // Remove SenderSignature from subject processing
    delete subjectAttrMap['{{sendersignature}}'];
    delete subjectAttrMap['{{SenderSignature}}'];

    // Parse spin tax BEFORE replacing attributes
    const subjectKeys = Object.keys(subjectAttrMap).map((key) =>
      key.toLowerCase(),
    );
    parsedSubject = getParsedSpinText(parsedSubject, subjectKeys, mergeTags);

    parsedSubject = replaceAttributesWithValues(
      parsedSubject,
      attributesMappingForRegex,
      attributesMappingWithValues,
      'subject',
    );

    return parsedSubject;
  };

  if (subject !== '') {
    return getParsedSubject(subject);
  }

  if (subject === '' && prevStepSubject !== '') {
    return getParsedSubject(prevStepSubject);
  }

  return subject;
};
